import React from 'react';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { clsx } from 'clsx';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'ghost' | 'link';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  icon?: IconName;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  fullWidth?: boolean;
  /**
   * Colapso do rótulo (F01-F03 + padrão do DS 2026-08-04). Dois regimes:
   * - `variant="primary"` (CTA): o rótulo RESISTE — span `ds-lbl` + hook
   *   `.ds-primary`, colapsando só pela cadeia @container do index.css
   *   (ds-tb 880 / hd-tb 900 via `hd-prim` / cl-tb 560).
   * - Demais variants (secundários): rótulo no padrão Tailwind
   *   `hidden 2xl:@tb:inline` — visível só em viewport ≥2xl E
   *   toolbar-container ≥ token `tb` (68rem); padding compacto no colapso.
   * O ícone permanece sempre visível. Sem a prop, nada muda.
   */
  collapsibleLabel?: boolean;
}

const LoadingSpinner: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={clsx('animate-spin', className)}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

// DS: flat (elevação = cor/borda, sem sombra), densidade (altura de controle 38px — `md` default,
// harmônico com Input/Select/MoneyInput/MultiSelect/Switch). `xs`/`sm` = contextos densos (toolbar/tabela).
// Cores via `--theme-*` (sistema único, dinâmico/white-label). Hover em CSS.
const variantClasses: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-[var(--theme-button-primary)] text-[var(--theme-button-primary-text)] hover:bg-[var(--theme-button-primary-hover)]',
  secondary:
    'bg-[var(--theme-button-secondary)] text-[var(--theme-button-secondary-text)] border border-[var(--theme-border)] hover:bg-[var(--theme-button-secondary-hover)]',
  ghost:
    'bg-transparent text-[var(--theme-text)] hover:bg-[var(--theme-button-ghost-hover)]',
  success: 'bg-[var(--theme-success-color)] text-white hover:opacity-90',
  warning: 'bg-[var(--theme-warning-color)] text-white hover:opacity-90',
  error: 'bg-[var(--theme-error-color)] text-white hover:opacity-90',
  link: 'bg-transparent text-[var(--theme-primary)] hover:underline',
};

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  xs: 'h-7 px-2 text-xs gap-1',
  sm: 'h-[30px] px-2.5 text-sm gap-1.5',
  md: 'h-[38px] px-4 text-sm gap-[7px]',
  lg: 'h-11 px-4 text-sm gap-2',
  xl: 'h-[52px] px-6 text-base gap-2',
};

// Secundário com rótulo colapsável (padrão do DS 2026-08-04): quando a legenda
// está oculta (<2xl ou toolbar estreita) o padding cai pra 10px (px-2.5),
// voltando ao padding do size junto com a legenda. Strings LITERAIS — o
// scanner do Tailwind não resolve template dinâmico.
const collapsibleSizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  xs: 'h-7 px-2 text-xs gap-1',
  sm: 'h-[30px] px-2.5 text-sm gap-1.5',
  md: 'h-[38px] px-2.5 2xl:@tb:px-4 text-sm gap-[7px]',
  lg: 'h-11 px-2.5 2xl:@tb:px-4 text-sm gap-2',
  xl: 'h-[52px] px-2.5 2xl:@tb:px-6 text-base gap-2',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    variant = 'primary',
    size = 'md',
    icon,
    iconPosition = 'left',
    loading = false,
    fullWidth = false,
    collapsibleLabel = false,
    className,
    children,
    disabled,
    style,
    ...props
  }, ref) => {
    // Rótulo colapsável (display:none tira o span do flex — o gap colapsa
    // junto; ícone fica). Primário resiste (ds-lbl + cadeia @container do
    // index.css); secundários seguem o padrão Tailwind do DS.
    const isCollapsiblePrimary = collapsibleLabel && variant === 'primary';
    const label = collapsibleLabel && children != null
      ? <span className={isCollapsiblePrimary ? 'ds-lbl' : 'hidden 2xl:@tb:inline'}>{children}</span>
      : children;
    // `display:none` tira o texto também da árvore de acessibilidade: no estado
    // só-ícone o botão ficaria sem accessible name. Fallback: `title` = próprio
    // rótulo (um `title` explícito em props vem depois no spread e vence).
    const fallbackTitle = collapsibleLabel && typeof children === 'string' ? children : undefined;
    return (
      <button
        ref={ref}
        className={clsx(
          'inline-flex items-center justify-center rounded-[9px] font-semibold whitespace-nowrap',
          'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring-color)]',
          'disabled:pointer-events-none disabled:opacity-50',
          variantClasses[variant],
          collapsibleLabel && !isCollapsiblePrimary ? collapsibleSizeClasses[size] : sizeClasses[size],
          { 'w-full': fullWidth },
          // Hook das chains: `.ds-tb/.cl-tb/.hd-tb .ds-primary` ajustam padding no colapso
          { 'ds-primary': isCollapsiblePrimary },
          className
        )}
        style={style}
        disabled={disabled || loading}
        title={fallbackTitle}
        {...props}
      >
        {loading && <LoadingSpinner className="h-4 w-4" />}
        {icon && !loading && iconPosition === 'left' && <Icon name={icon} className="h-4 w-4" />}
        {label}
        {icon && !loading && iconPosition === 'right' && <Icon name={icon} className="ml-1 h-4 w-4" />}
      </button>
    );
  }
);

Button.displayName = 'Button';
